import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Pin } from "lucide-react";

interface AddKnowledgeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: number;
  onCreated: () => void;
}

export function AddKnowledgeModal({ open, onOpenChange, brandId, onCreated }: AddKnowledgeModalProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/chunks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          category: "user_pin",
          importance: 10,
        }),
      });
      if (res.ok) {
        setTitle("");
        setContent("");
        onOpenChange(false);
        onCreated();
      } else {
        setError("Failed to save knowledge. Please try again.");
      }
    } catch (e) {
      setError("Network error. Please check your connection.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pin className="h-4 w-4 text-blue-500" />
            Add Knowledge
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Title</label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g., Target audience insight"
              className="mt-1"
              data-testid="input-knowledge-title"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Content</label>
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="What should the AI remember?"
              className="mt-1 min-h-[120px]"
              data-testid="input-knowledge-content"
            />
          </div>
          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
          )}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                Pinned
              </span>
              <span className="text-xs text-gray-400">Importance: 10</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="button-cancel-knowledge">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!title.trim() || !content.trim() || saving}
                className="bg-indigo-500 hover:bg-indigo-600"
                data-testid="button-save-knowledge"
              >
                {saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
